import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Header, Footer } from '../../components/layout';
import { ArrowLeft, Play, MessageCircle, FileText, Zap, CheckCircle, Clock, Users, Target, Lightbulb, ExternalLink, Briefcase, Repeat, Star } from 'lucide-react';
import LatteIcon from '../../components/common/LatteIcon';
import LatteButton from '../../components/common/LatteButton';
import '../../styles/desktop/UserGuidePage.css';

const UserGuidePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const FeatureCard = ({ icon: IconComponent, title, description, highlight = false, isLatte = false }) => (
    <div className={`feature-card ${
      isLatte 
        ? 'border-latte-light bg-gradient-to-br from-latte-lightest to-orange-50 hover:border-latte-primary' 
        : highlight 
        ? 'border-blue-200 bg-blue-50 hover:border-blue-300' 
        : 'border-gray-200 bg-white hover:border-gray-300'
    }`}>
      <div className="flex items-start gap-4">
        <div className={`feature-card-icon ${
          isLatte ? 'bg-latte-light' : highlight ? 'bg-blue-100' : 'bg-gray-100'
        }`}>
          <IconComponent className={`w-6 h-6 ${
            isLatte ? 'text-latte-primary' : highlight ? 'text-blue-600' : 'text-gray-600'
          }`} />
        </div>
        <div>
          <h3 className="feature-card-title">{title}</h3>
          <p className="feature-card-description">{description}</p>
        </div>
      </div>
    </div>
  );

  const StepCard = ({ number, title, description, tips = [], isLatte = false }) => (
    <div className={`step-card ${
      isLatte 
        ? 'bg-gradient-to-br from-latte-lightest to-orange-50 border-latte-light' 
        : 'bg-white border-gray-200'
    }`}>
      <div className="flex items-start gap-4">
        <div className={`step-card-number ${
          isLatte 
            ? 'bg-latte-primary text-white' 
            : 'bg-gray-900 text-white'
        }`}>
          {number}
        </div>
        <div className="flex-1">
          <h3 className="step-card-title">{title}</h3>
          <p className="step-card-description">{description}</p>
          {tips.length > 0 && (
            <div className="step-card-tips">
              {tips.map((tip, index) => (
                <div key={index} className="step-card-tip">
                  <Lightbulb className="w-4 h-4 mt-0.5 text-latte-primary" />
                  <span>{tip}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const FAQItem = ({ question, answer }) => (
    <div className="faq-item">
      <h3 className="faq-question">{question}</h3>
      <p className="faq-answer">{answer}</p>
    </div>
  );

  return (
    <div className="page-container-white">
      {user ? (
        <Header title="사용자 가이드" />
      ) : (
        <div className="header">
          <div className="header-container">
            <div className="header-left">
              <div className="header-logo">
                <LatteIcon size="lg" />
                <button 
                  onClick={() => navigate('/login')}
                  className="header-title"
                  title="로그인 페이지로 이동"
                >
                  Latte is
                </button>
              </div>
              <span className="header-separator">|</span>
              <span className="text-lg text-gray-600">사용자 가이드</span>
            </div>
            <LatteButton
              onClick={() => navigate('/login')}
              variant="primary"
            >
              로그인
            </LatteButton>
          </div>
        </div>
      )}
      
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* 헤더 */}
        <div className="mb-8">
          <button 
            onClick={() => user ? (window.history.length > 1 ? navigate(-1) : navigate('/dashboard')) : navigate('/login')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {user ? '이전으로' : '로그인으로'}
          </button>
          
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <LatteIcon size="xl" />
              <h1 className="text-4xl font-bold text-gray-900">
                Latte is 사용자 가이드
              </h1>
            </div>
            <p className="text-lg text-amber-600 font-medium mb-4 italic">
              Latte becomes knowledge
            </p>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              경력자의 소중한 경험담이 AI를 만나 이력서를 시작으로 개인 브랜딩까지 완성됩니다
            </p>
          </div>
        </div>

        {/* latteis 소개 */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-amber-600 to-orange-700 rounded-2xl p-8 text-white mb-8">
            <div className="flex items-center gap-3 mb-4">
              <LatteIcon size="lg" className="text-amber-100" />
              <h2 className="text-3xl font-bold">Latte is는 무엇인가요?</h2>
            </div>
            <p className="text-xl text-amber-100 mb-6">
              <strong>"라떼는 말이지, 우리 때는..."</strong> 바로 당신의 그 '라떼'가 가장 확실한 '스펙'이 됩니다.
              AI가 당신의 모든 경험을 놓치지 않고 가치 있는 데이터로 전환하고, 그 데이터는 곧 당신의 새로운 이력서이자 퍼스널 브랜딩의 핵심 자산이 될 것입니다.
            </p>
            
            <div className="bg-amber-500/30 rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Target className="w-5 h-5" />
                이런 분들에게 딱 맞아요
              </h3>
              <ul className="space-y-2 text-amber-100">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  수많은 경력과 성과를 단순 나열하는 것을 넘어, 하나의 강력한 '성공 서사'로 엮어내고 싶은 분
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  자신의 큰 성과에 대해 "이건 뭐 당연히 한 일이지"라고 생각하며, 그 안에 숨은 비즈니스 가치를 놓치고 있는 분
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  단순 이직이 아닌, 다음 커리어 단계(리더, 임원, 창업 등)로의 도약을 위해 자신의 모든 것을 '전략 자산'으로 재구성하고 싶은 분
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  자신만의 노하우와 문제 해결 능력을 이력서에 담아내는 것을 넘어, 후배들에게 영감을 줄 수 있는 블로그나 책의 형태로 남기고 싶은 분
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* 핵심 특징 */}
        <section className="mb-16">
          <div className="flex items-center justify-center gap-3 mb-8">
            <LatteIcon size="lg" />
            <h2 className="text-3xl font-bold text-gray-900">Latte is만의 특별함</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <FeatureCard
              icon={Repeat}
              title="옛날 이야기를 전문성으로 번역"
              description="'그 때는 말이지...'로 시작하는 경험담을 AI가 STAR 기법(Situation, Task, Action, Result)과 정량적 성과로 번역해드립니다. 단순한 옛날 이야기가 채용담당자가 인정하는 전문성으로 변환됩니다."
              isLatte={true}
            />
            <FeatureCard
              icon={MessageCircle}
              title="기억 발굴 중심 대화"
              description="단순한 정보 수집이 아닌, 사용자의 기억을 체계적으로 되살리는 질문 시퀀스. '잘 기억이 안나요'에 대비한 맥락적 기억 회복 질문들로 숨겨진 성과를 찾아드립니다."
              isLatte={true}
            />
            <FeatureCard
              icon={FileText}
              title="실시간 이력서 생성"
              description="대화 내용이 실시간으로 이력서에 반영됩니다. 현재 업데이트되는 부분이 시각적으로 표시되어 진행 상황을 바로 확인할 수 있습니다."
              isLatte={true}
            />
            <FeatureCard
              icon={Target}
              title="성과 구체화 엔진"
              description="'효율적이었어요'를 '개발 시간 50% 단축'으로, 모호한 표현을 STAR 기법을 활용해 정량적 지표로 변환합니다."
              isLatte={true}
            />
            <FeatureCard
              icon={Briefcase}
              title="3가지 출력 형태"
              description="한 번의 대화로 PDF 이력서, AI 번역 영어 이력서, 그리고 개인 URL 웹페이지까지 모두 완성됩니다. 웹페이지에는 이력서뿐만 아니라 AI가 생성한 지식 블로그 포스트들도 함께 공개됩니다."
              isLatte={true}
            />
             <FeatureCard
              icon={Star}
              title="AI 지식 블로그 자동 생성 (8월 오픈)"
              description="이력서 컨설팅에서 나눠 대화를 AI가 분석해 여러 개의 블로그 포스트로 자동 변환합니다. '시스템 아키텍처 개선기', '팀 리드의 성장 스토리' 같은 포스트가 AI에 의해 자동 생성되고, 각 포스트마다 음성으로 추가 스토리를 계속 더해갈 수 있습니다. 당신의 '라떼'가 후배들을 위한 살아있는 지식이 됩니다."
              isLatte={true}
            />
            
          </div>
        </section>

        {/* 사용 방법 */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">이용 방법</h2>
          <div className="space-y-8">
            <StepCard
              number="1"
              title="회원가입 및 컨설팅 시작"
              description="구글/깃허브 소셜 로그인 후 'AI 컨설팅 시작하기' 버튼을 클릭하세요. 이름을 입력하면 바로 컨설팅이 시작됩니다."
              tips={["첫 이용이라면 5분 정도 여유를 두고 시작하세요"]}
            />
            <StepCard
              number="2"
              title="자연스러운 경험담 대화"
              description="편안하게 경험담을 들려주세요. AI가 경청하며 숨겨진 전문성을 찾아드립니다. '잘 기억이 안나요'라고 하셔도 괜찮습니다."
              tips={[
                "과거와 현재를 비교하며 말씀해주세요",
                "어려웠던 상황일수록 좋은 성과 포인트가 될 수 있어요"
              ]}
            />
            <StepCard
              number="3"
              title="실시간 이력서 확인"
              description="우측 화면에서 대화 내용이 실시간으로 이력서에 반영되는 것을 확인하세요. 현재 업데이트되는 부분이 초록색으로 하이라이트됩니다."
              tips={["확대/축소 버튼으로 이력서를 더 자세히 볼 수 있어요"]}
            />
            <StepCard
              number="4"
              title="편집 및 수정"
              description="'이력서 보기' 버튼으로 편집 페이지에서 세부 내용을 직접 수정할 수 있습니다. AI가 작성한 내용을 원하는 대로 커스터마이징하세요."
              tips={["편집 내용은 자동 저장되어 언제든 다시 수정 가능합니다"]}
            />
            <StepCard
              number="5"
              title="다양한 형태로 활용하기"
              description="완성된 이력서를 3가지 형태로 즉시 활용할 수 있습니다. PDF 다운로드로 즉시 이력서를 저장하여 이메일이나 채용 사이트에 첨부하고, AI가 전문적인 영어 이력서로 번역하여 글로벌 기업 지원 시 활용하며, 나만의 URL로 온라인 이력서 페이지를 생성하여 링크로 공유할 수 있습니다."
              tips={[
                "각 형태는 용도에 맞게 최적화되어 있어 상황에 따라 선택하여 사용하세요",
                "웹페이지에는 7월부터 AI가 생성한 블로그 포스트가 추가됩니다"
              ]}
            />
          </div>
        </section>

        {/* 효과적인 이용 팁 */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">효과적인 컨설팅 팁</h2>
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-blue-600" />
                대화 스타일
              </h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <div>
                    <strong>자연스럽게:</strong>
                    <span className="block text-sm mt-1">"그냥 일했을 뿐"이라고 하셔도 AI가 숨겨진 성과를 찾아드립니다</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <div>
                    <strong>구체적으로:</strong>
                    <span className="block text-sm mt-1">프로젝트 규모, 팀 크기, 기간 등 숫자를 말씀해주세요</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <div>
                    <strong>솔직하게:</strong>
                    <span className="block text-sm mt-1">기억이 안 나시는 부분은 솔직히 말씀하세요</span>
                  </div>
                </li>
              </ul>
            </div>
            <div className="bg-white p-6 rounded-xl border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-green-600" />
                성과 표현
              </h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <div>
                    <strong>Before & After:</strong>
                    <span className="block text-sm mt-1">개선 전후 상황 비교</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <div>
                    <strong>정량적 지표:</strong>
                    <span className="block text-sm mt-1">시간 단축, 비용 절감, 매출 증가</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <div>
                    <strong>팀 임팩트:</strong>
                    <span className="block text-sm mt-1">팀 생산성, 협업 개선, 멘토링 성과</span>
                  </div>
                </li>
              </ul>
            </div>
            <div className="bg-white p-6 rounded-xl border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-purple-600" />
                기억 회복
              </h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <div>
                    <strong>배경부터:</strong>
                    <span className="block text-sm mt-1">"어떤 문제를 해결하려고 했나요?"</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <div>
                    <strong>기술부터:</strong>
                    <span className="block text-sm mt-1">"어떤 방법론/프레임워크를 사용했나요?"</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <div>
                    <strong>팀 구성부터:</strong>
                    <span className="block text-sm mt-1">"팀은 몇 명이었고, 어떤 역할이었나요?"</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">자주 묻는 질문</h2>
          <div className="bg-white rounded-2xl p-8 border border-gray-200">
            <FAQItem
              question="과거 경험담도 괜찮나요?"
              answer="네! '예전에는 이랬는데, 지금은...'하는 비교 이야기에서 시대 변화에 대한 적응력과 경험의 깊이를 찾을 수 있어요. AI는 여러분의 경험담을 전문성으로 번역하는 전문가입니다."
            />
            <FAQItem
              question="구체적으로 기억이 안 나는 부분이 있어도 괜찮을까요?"
              answer="전혀 문제없습니다! 그 뒤에 숨어있는 전문 지식과 노하우를 AI가 알아서 찾아드립니다. 중요한 건 솔직한 경험담입니다."
            />
            <FAQItem
              question="컨설팅은 얼마나 오래 걸리나요?"
              answer="평균 45-60분 정도입니다. 경력이 많으실수록 조금 더 오래 걸릴 수 있어요. 중간에 언제든 저장하고 나중에 이어서 할 수 있습니다."
            />
            <FAQItem
              question="음성 인식이 잘못될 때는 어떻게 하나요?"
              answer="AI가 문맥을 파악해서 자동으로 수정하거나, 텍스트로 다시 입력하시면 됩니다. 중요한 내용은 텍스트로 보완 설명하시는 것을 추천합니다."
            />
            <FAQItem
              question="개인정보는 안전한가요?"
              answer="모든 데이터는 암호화되어 저장되며, 오직 이력서 작성 목적으로만 사용됩니다. 사용자의 동의 없이 다른 용도로 사용되지 않습니다."
            />
            <FAQItem
              question="수정 후 다시 PDF를 받을 수 있나요?"
              answer="네, 언제든지 수정 후 재다운로드 가능합니다. '이전 이력서 목록'에서 모든 버전을 관리할 수 있어요."
            />
            <FAQItem
              question="영어 이력서는 어떻게 만드나요?"
              answer="한국어 이력서 완성 후 '영어 이력서 변환' 버튼만 클릭하면 AI가 전문적인 영어 이력서로 즉시 번역해드립니다. 글로벌 기업 지원이나 해외 진출 시 바로 활용 가능합니다."
            />
            <FAQItem
              question="PDF, 영어 이력서, 웹페이지의 차이점은?"
              answer="PDF는 일반적인 이력서 제출용, 영어 이력서는 글로벌 기업이나 해외 지원용, 웹페이지는 링크 공유와 온라인 포트폴리오용입니다. 특히 웹페이지는 현재 개인 URL로 이력서를 공유할 수 있고, 7월부터는 AI가 자동 생성한 블로그 포스트들도 함께 공개되어 통합 플랫폼이 됩니다."
            />
            <FAQItem
              question="웹페이지 URL은 어떻게 설정하나요?"
              answer="웹페이지 생성 시 원하는 주소를 설정할 수 있습니다. https://latteis.com/yourname 형태로 생성되며, 이 링크를 채용담당자나 동료들과 공유하면 언제든 온라인에서 이력서를 확인할 수 있습니다."
            />
            <FAQItem
              question="AI 지식 블로그는 어떻게 작동하나요?"
              answer="이력서 컨설팅에서 나눈 30분의 대화를 AI가 분석해 여러 개의 블로그 포스트로 자동 변환합니다. 예를 들어 '신입 시절 야근 스토리'가 '주니어 개발자의 성장기'라는 포스트로, '팀 리드 경험'이 '효과적인 팀 리듩 노하우'라는 포스트로 변환됩니다. 각 포스트마다 음성으로 추가 이야기를 계속 더해가며 콘텐츠를 풍부하게 만들 수 있습니다."
            />
          </div>
        </section>

        {/* 성공 사례 */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Latte is 활용 성공 사례</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-amber-50 to-orange-100 p-6 rounded-xl border border-amber-200">
              <div className="flex items-center gap-2 mb-3">
                <LatteIcon size="sm" />
                <h3 className="text-lg font-semibold text-amber-800">당연한 일이었는데...</h3>
              </div>
              <p className="text-amber-700 italic leading-relaxed">
                "그냥 당연히 하는 거였는데... 라고 생각했던 업무가 실제로는 회사 혁신을 이끈 핵심 성과였다는 걸 발견했어요."
              </p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200">
              <h3 className="text-lg font-semibold text-blue-800 mb-3">정량적 성과 구체화</h3>
              <p className="text-blue-700 italic leading-relaxed">
                "'효율적으로 개발했다'라는 모호한 표현이 '개발 속도 50% 향상 및 버그 발생률 80% 감소'라는 구체적인 성과로 바뀌었습니다."
              </p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl border border-green-200">
              <h3 className="text-lg font-semibold text-green-800 mb-3">경험담의 재발견</h3>
              <p className="text-green-700 italic leading-relaxed">
                "경험담이 체계적으로 정리되어, 단순한 경력자가 아닌 시대 변화를 이끌어온 전문가로서의 진짜 모습이 드러났어요."
              </p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center">
          <div className="bg-gradient-to-r from-amber-600 to-orange-700 rounded-2xl p-12 text-white">
            <div className="flex items-center justify-center gap-3 mb-4">
              <LatteIcon size="lg" className="text-amber-100" />
              <h2 className="text-3xl font-bold">이제 시작할 때입니다!</h2>
            </div>
            <p className="text-xl text-amber-100 mb-8 max-w-2xl mx-auto">
              당신의 소중한 경험담을 AI가 들어드립니다.<br/>
              30분 후에는 채용담당자가 주목하는 이력서가 완성됩니다.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => user ? navigate('/dashboard') : navigate('/login')}
                className="inline-flex items-center gap-3 bg-white text-amber-600 px-8 py-4 rounded-xl hover:bg-gray-50 transition-colors font-semibold text-lg"
              >
                <Play className="w-6 h-6" />
                {user ? 'Latte is 시작하기' : '로그인 후 시작하기'}
              </button>
            </div>
          </div>
        </section>
      </div>
      
      <Footer />
    </div>
  );
};

export default UserGuidePage;